import EditOutlined from '@ant-design/icons/EditOutlined';
import { theme } from 'antd/es';
import Button from 'antd/es/button';
import Typography from 'antd/es/typography';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { EditButton, TitleWrapper } from './TaskHeader';

import MarkdownContent from '@components/MarkdownContent';

const { Title } = Typography;
const { useToken } = theme;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const SectionTitle = styled(Title)`
  margin-bottom: 0 !important;
  margin-top: 0 !important;
`;

const ContentWrapper = styled.div`
  position: relative;
`;

const DescriptionContainer = styled.div<{ isExpanded: boolean }>`
  position: relative;
  max-height: ${(props) => (props.isExpanded ? 'none' : '200px')};
  overflow: hidden;
`;

const FadeGradient = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 40px;
  pointer-events: none;
`;

const ShowMoreButton = styled(Button)`
  display: block;
  margin-top: 8px;
  position: relative;
  z-index: 2;
`;

interface TaskDescriptionProps {
  description: string;
  onEditDescription: () => void;
}

const TaskDescription: React.FC<TaskDescriptionProps> = ({
  description,
  onEditDescription,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const { token } = useToken();

  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        const hasContentOverflow = contentRef.current.scrollHeight > 200;
        setHasOverflow(hasContentOverflow);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [description]);

  return (
    <div>
      <SectionHeader>
        <TitleWrapper>
          <SectionTitle level={4} style={{ margin: 0 }}>
            Description
          </SectionTitle>
          <EditButton
            type="text"
            icon={<EditOutlined />}
            size="small"
            aria-label="Edit description"
            onClick={onEditDescription}
          />
        </TitleWrapper>
      </SectionHeader>
      <ContentWrapper>
        <DescriptionContainer isExpanded={isExpanded} ref={contentRef}>
          <MarkdownContent content={description} />
        </DescriptionContainer>
        {!isExpanded && hasOverflow && (
          <FadeGradient
            style={{
              background: `linear-gradient(
                rgba(${
                  token.colorBgContainer
                    .replace(/^#/, '')
                    .match(/../g)
                    ?.map((hex) => parseInt(hex, 16))
                    .join(', ') || '255, 255, 255'
                }, 0),
                ${token.colorBgContainer}
              )`,
            }}
          />
        )}
      </ContentWrapper>
      {hasOverflow && (
        <ShowMoreButton type="link" onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? 'Show Less' : 'Show Full Description'}
        </ShowMoreButton>
      )}
    </div>
  );
};

export default TaskDescription;
export { SectionHeader, SectionTitle };
